import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Autocomplete,
  CircularProgress,
  Chip,
  Button,
  Avatar,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  InputAdornment,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import moment from 'moment';
import axios from 'axios';
import { APIURL } from '../../config';
import { Form as FormioForm } from '@formio/react';
import SocialCareReferralForm from './SocialCareReferralForm';
import OBForm from './OBForm.js';

const SUBTABS = { INTERNAL: 0, EXTERNAL: 1 };

const CreateReferral = ({ comp_id, user, com_name, preselectedCustomer, onSuccess }) => {
  const [subTab, setSubTab] = useState(SUBTABS.INTERNAL);

  // Company search state
  const [companyInput, setCompanyInput] = useState('');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null); // { id, name, guid }
  // Cache of company logos by company_id: { [company_id]: dataUri | null }
  const [companyLogos, setCompanyLogos] = useState({});
  const logoFetchRef = useRef({}); // tracks in-flight/attempted fetches

  // External company fields
  const extCompanyNameRef = useRef('');
  const extCompanyAddressRef = useRef('');
  const extCompanyEmailRef = useRef('');
  const extCompanyPhoneRef = useRef('');
  const [, forceExternalFieldsUpdate] = useState(0);
  const [extSubmitting, setExtSubmitting] = useState(false);
  // External dynamic form (initiator's external form)
  const [externalFormSchema, setExternalFormSchema] = useState(null);
  const [externalFormLoading, setExternalFormLoading] = useState(false);
  const [externalFormError, setExternalFormError] = useState(null);
  const [extSocialForm, setExtSocialForm] = useState({});
  // Flag: external company was chosen from Company Search (hide required fields UI and skip gating)
  const [externalSelectedFromSearch, setExternalSelectedFromSearch] = useState(false);

  // Dynamic incoming form for selected internal company
  const [incomingFormSchema, setIncomingFormSchema] = useState(null);
  const [incomingFormLoading, setIncomingFormLoading] = useState(false);
  const [incomingFormError, setIncomingFormError] = useState(null);
  const [outgoingSubmissionData, setOutgoingSubmissionData] = useState(null);
  const [activeSetting, setActiveSetting] = useState(null);

  // Company name comes from props (initiator) for inbound task handler
  const obFormDataRef = useRef({});

  // Referring customer (from initiating company) dropdown state
  const [refCustomerOptions, setRefCustomerOptions] = useState([]); // [{ cus_id, name, addressString }]
  const [refCustomerLoading, setRefCustomerLoading] = useState(false);
  const [refCustomerError, setRefCustomerError] = useState(null);
  const selectedRefCustomerRef = useRef(null); // { cus_id, name, addressString }
  const [refCustomerValidationError, setRefCustomerValidationError] = useState(null);

  // Refs

  // Submission success state
  const [submitted, setSubmitted] = useState(false);
  const usingFormio = !!incomingFormSchema;

  const resetInternal = () => {
    setSelectedCompany(null);
    setIncomingFormSchema(null);
    setIncomingFormError(null);
    setOutgoingSubmissionData(null);
    obFormDataRef.current = {};
    selectedRefCustomerRef.current = null;
    setRefCustomerValidationError(null);
  };

  const clearExternalSelection = () => {
    extCompanyNameRef.current = '';
    extCompanyAddressRef.current = '';
    extCompanyEmailRef.current = '';
    extCompanyPhoneRef.current = '';
    setExternalSelectedFromSearch(false);
    forceExternalFieldsUpdate((tick) => tick + 1);
  };

  // External referral submit
  const handleExternalReferralSubmit = async (e, dataObj) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    // Require referring customer selection
    if (!(selectedRefCustomerRef.current && selectedRefCustomerRef.current.cus_id)) {
      setRefCustomerValidationError('Please select the customer to refer.');
      alert('Please select the customer to refer.');
      return;
    }
    const name = (extCompanyNameRef.current || '').trim();
    const addr = (extCompanyAddressRef.current || '').trim();
    const email = (extCompanyEmailRef.current || '').trim();
    const phone = (extCompanyPhoneRef.current || '').trim();
    if (!externalSelectedFromSearch) {
      if (!(name && addr && email && phone)) {
        console.log(name, addr, email, phone);

        alert('Fill required Generic Referral details before submitting.');
        return;
      }
    }
    try {
      setExtSubmitting(true);
      // Backend expects external_company_name or external_company_id at the root
      const payload = {
        company_id: comp_id,
        external_as: 'receiver',
        external_company_name: name || 'Generic Referral',
        referral_subject: 'Referral',
        referral_date: moment().format('YYYY-MM-DD'),
        referral_status: 'PENDING',
        data: {
          ...(dataObj || {}),
          ...(selectedRefCustomerRef.current?.cus_id ? { referring_customer_id: selectedRefCustomerRef.current.cus_id, referring_customer_name: selectedRefCustomerRef.current.name } : {}),
          // Preserve external contact details when manually entered
          ...(externalSelectedFromSearch ? {} : {
            external_company_address: addr,
            external_company_phone: phone,
            external_company_email: email
          })
        },
        employee_id: user?.emp_id || undefined,
        employee_name: user?.emp_name || undefined,
        employee_email: user?.email || undefined,
        customer_id: selectedRefCustomerRef.current?.cus_id || undefined,
        customer_name: selectedRefCustomerRef.current?.name || undefined,
        customer_email: selectedRefCustomerRef.current?.email || undefined,
      };
      const resp = await axios.post(`${APIURL}/referral/external`, payload, { headers: { 'Content-Type': 'application/json' } });
      const ok = (resp?.status >= 200 && resp?.status < 300) || resp?.data?.code === 200;
      console.log("OK", ok);

      if (ok) {
        setSubmitted(true);
        if (onSuccess) onSuccess();
        // Attempt outbound referral task creation for initiating company
        try {
          let response = await axios.post(`${APIURL}/referral/outboundTask`, {
            company_id: comp_id,
            employee_id: user?.emp_id,
            customer_id: selectedRefCustomerRef.current?.cus_id,
            receiving_company_name: extCompanyNameRef.current || undefined
          }, { headers: { 'Content-Type': 'application/json' } });
          console.log('Created outbound referral task (external)', response?.data);
        } catch (outErr) {
          // Non-blocking if no active project found for customer
          console.log('Failed to create outbound referral task (external)', outErr?.response?.data || outErr?.message);
        }
      } else {
        alert('Failed to submit Generic Referral');
      }
    } catch (err) {
      alert('Error submitting Generic Referral');
    } finally {
      setExtSubmitting(false);
    }
  };

  // Fetch + parse a Form.io schema by form_id. Returns parsed schema or null.
  const fetchFormSchemaById = useCallback(async (formId) => {
    if (!formId) return null;
    const formResp = await axios.get(`${APIURL}/task_form/form`, { params: { form_id: formId } });
    const rawSchema = formResp.data?.[0]?.json_schema;
    const parsed = typeof rawSchema === 'string' ? JSON.parse(rawSchema) : rawSchema;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.display) parsed.display = 'form';
    if (!Array.isArray(parsed.components)) parsed.components = [];
    return parsed;
  }, []);

  // Safely fetch an active referral_setting; returns the setting object or null (no throw on 404).
  const fetchActiveSetting = useCallback(async (companyId) => {
    if (!companyId) return null;
    try {
      const resp = await axios.get(`${APIURL}/referralSettings/${companyId}/active`);
      return resp.data || null;
    } catch (e) {
      if (e?.response?.status === 404) return null;
      throw e;
    }
  }, []);

  // Load our own company's outgoing form on mount (used as the generic/external form)
  useEffect(() => {
    const loadExternalForm = async () => {
      if (!comp_id) return;
      setExternalFormError(null);
      setExternalFormSchema(null);
      setExternalFormLoading(true);
      try {
        const setting = await fetchActiveSetting(comp_id);
        const formId = setting?.referral_form_outgoing_id || setting?.referral_form_external_id;
        if (!formId) { setExternalFormSchema(null); return; }
        const schema = await fetchFormSchemaById(formId);
        setExternalFormSchema(schema || null);
        if (!schema) setExternalFormError(null); // no schema → fall back to SocialCareReferralForm silently
      } catch (err) {
        if (err?.response?.status !== 404) setExternalFormError('Failed to load Generic Referral form');
        setExternalFormSchema(null);
      } finally {
        setExternalFormLoading(false);
      }
    };
    loadExternalForm();
  }, [comp_id, fetchActiveSetting, fetchFormSchemaById]);

  // Load initiating company's customers for the dropdown
  useEffect(() => {
    const loadRefCustomers = async () => {
      if (!comp_id) return;
      setRefCustomerError(null);
      setRefCustomerLoading(true);
      try {
        let rows = [];

        const type = user?.permissions?.AdminLevelReferral ? 'Admin' : 'Manager';
        const resp = await axios.get(`${APIURL}/customers/v2/associated`, {
          params: { company_id: comp_id, employee_id: user.emp_id, type }
        });
        console.log('Associated customers response', resp);

        rows = (resp?.data || []).filter((r) => String(r.cus_status || '').toLowerCase() === 'active');

        const mapped = rows.map(r => ({ cus_id: r.cus_id, name: r.cus_name, addressString: r.cus_address, email: r.cus_email }));
        setRefCustomerOptions(Array.isArray(mapped) ? mapped : []);
        // If preselectedCustomer is provided, set it as the selected customer only if it is active
        if (preselectedCustomer?.cus_id) {
          console.log('preselectedCustomer', preselectedCustomer);
          const activePreselected = mapped.find((customer) => String(customer.cus_id) === String(preselectedCustomer.cus_id));
          selectedRefCustomerRef.current = activePreselected || null;
        } else {
          selectedRefCustomerRef.current = null;
        }
      } catch (err) {
        setRefCustomerOptions([]);
        setRefCustomerError('Failed to load customers');
      } finally {
        setRefCustomerLoading(false);
      }
    };
    loadRefCustomers();
  }, [comp_id, user]);

  // Company search handlers (adapted to internal API)
  const fetchCompanyLogo = useCallback(async (companyId) => {
    if (!companyId) return;
    if (logoFetchRef.current[companyId]) return; // already fetched/attempted
    logoFetchRef.current[companyId] = true;
    try {
      const resp = await axios.get(`${APIURL}/company/logo/${companyId}`);
      if (resp?.data?.logo?.data) {
        let binary = '';
        const bytes = new Uint8Array(resp.data.logo.data);
        bytes.forEach((b) => { binary += String.fromCharCode(b); });
        const dataUri = `data:${resp.data.type || 'image/png'};base64,${window.btoa(binary)}`;
        setCompanyLogos((prev) => ({ ...prev, [companyId]: dataUri }));
      } else {
        setCompanyLogos((prev) => ({ ...prev, [companyId]: null }));
      }
    } catch (err) {
      setCompanyLogos((prev) => ({ ...prev, [companyId]: null }));
    }
  }, []);

  const handleCompanyInputChange = async (_event, value, reason) => {
    if (reason === 'clear' || (reason === 'input' && !value)) {
      setCompanyInput('');
      setCompanyOptions([]);
      setCompanyError(null);
      // Reset selection → return to generic form view
      setSelectedCompany(null);
      setIncomingFormSchema(null);
      setIncomingFormError(null);
      setExternalSelectedFromSearch(false);
      extCompanyNameRef.current = '';
      setOutgoingSubmissionData(null);
      obFormDataRef.current = {};
      return;
    }
    if (reason === 'input') {
      setCompanyInput(value);
    } else {
      return; // ignore reset/blur
    }
    setCompanyError(null);
    if (!value || value.trim().length < 2) return;
    setCompanyLoading(true);
    try {
      const resp = await axios.get(`${APIURL}/referralSettings/company/search`, { params: { q: value.trim() } });
      console.log('Company search response', resp );
      
      if (!Array.isArray(resp.data)) {
        console.warn('[CreateReferral] company search returned non-array payload:', resp.data);
      }
      let rows = Array.isArray(resp.data) ? resp.data : [];
      if (comp_id) {
         rows = rows.filter(row => String(row.company_id) !== String(comp_id));
      }
      setCompanyOptions(rows);
      // Fetch logos for internal companies (those with a company_id)
      rows.forEach((row) => { if (row.company_id) fetchCompanyLogo(row.company_id); });
    } catch (err) {
      setCompanyError('Error fetching companies');
      setCompanyOptions([]);
    } finally {
      setCompanyLoading(false);
    }
  };

  const loadIncomingFormForCompany = useCallback(async (targetCompanyId) => {
    if (!targetCompanyId) return;
    setIncomingFormError(null);
    setIncomingFormLoading(true);
    setIncomingFormSchema(null);
    setOutgoingSubmissionData(null);
    obFormDataRef.current = {};
    try {
      // 1) Target company's INCOMING referral form
      console.log('[Referral] Fetching active setting for target company:', targetCompanyId);
      const targetSetting = await fetchActiveSetting(targetCompanyId);
      setActiveSetting(targetSetting);
      console.log('[Referral] Target active setting:', targetSetting);

      const incomingFormId = targetSetting?.referral_form_incoming_id || targetSetting?.referral_form_id;
      if (incomingFormId) {
        console.log('[Referral] Using target incoming form id:', incomingFormId);
        const schema = await fetchFormSchemaById(incomingFormId);
        if (schema) { setIncomingFormSchema(schema); return; }
        console.warn('[Referral] Target incoming form id has no valid schema:', incomingFormId);
      } else {
        console.log('[Referral] Target has no incoming form configured.');
      }

      // 2) Fallback: OUR OWN company's OUTGOING referral form
      console.log('[Referral] Falling back to own company outgoing form. Own company:', comp_id);
      const ownSetting = await fetchActiveSetting(comp_id);
      console.log('[Referral] Own active setting:', ownSetting);
      const outgoingFormId = ownSetting?.referral_form_outgoing_id || ownSetting?.referral_form_external_id;
      if (outgoingFormId) {
        console.log('[Referral] Using own outgoing form id:', outgoingFormId);
        const schema = await fetchFormSchemaById(outgoingFormId);
        if (schema) { setIncomingFormSchema(schema); return; }
        console.warn('[Referral] Own outgoing form id has no valid schema:', outgoingFormId);
      } else {
        console.log('[Referral] Own company has no outgoing form configured.');
      }

      // 3) Neither configured — render OBForm fallback
      console.log('[Referral] No custom form available; using OBForm fallback.');
      setIncomingFormSchema(null);
    } catch (e) {
      console.error('[Referral] Error in loadIncomingFormForCompany:', e?.response?.status, e?.message, e);
      setIncomingFormError(`Failed to load referral form (${e?.response?.status ?? e?.message ?? 'unknown error'})`);
      setIncomingFormSchema(null);
    } finally {
      setIncomingFormLoading(false);
    }
  }, [comp_id, fetchActiveSetting, fetchFormSchemaById]);

  const handleCompanySelect = async (_event, value) => {
    if (!value) return;
    if (value.source_type === 'external') {
      // Clear any previously selected internal company
      setSelectedCompany(null);
      setIncomingFormSchema(null);
      setIncomingFormError(null);
      setOutgoingSubmissionData(null);
      obFormDataRef.current = {};
      // Set external state
      setSubTab(SUBTABS.EXTERNAL);
      extCompanyNameRef.current = value.company_name || '';
      forceExternalFieldsUpdate((tick) => tick + 1);
      setExternalSelectedFromSearch(true);
      // If subTab is already EXTERNAL, the useEffect won't re-fire — load manually
      if (subTab === SUBTABS.EXTERNAL) {
        setExternalFormSchema(null);
        setExternalFormError(null);
        setExternalFormLoading(true);
        try {
          const setting = await fetchActiveSetting(comp_id);
          const formId = setting?.referral_form_outgoing_id || setting?.referral_form_external_id;
          const schema = formId ? await fetchFormSchemaById(formId) : null;
          setExternalFormSchema(schema);
        } catch (err) {
          setExternalFormError('Failed to load referral form');
        } finally {
          setExternalFormLoading(false);
        }
      }
      return;
    }
    // Internal company: clear any external selection first
    setExternalSelectedFromSearch(false);
    extCompanyNameRef.current = '';
    // Internal company: show form inline
    const directId = value.company_id || value.com_id || value.companyid;
    const name = value.company_name || value.com_name || '';
    const guid = value.company_guid || value.com_guid || '';
    if (directId) {
      setSelectedCompany({ id: directId, name, guid });
      await loadIncomingFormForCompany(directId);
    }
  };

  // Removed manual address autocomplete; using selected customer's address

  // Internal dynamic form submit (used for both Form.io and OBForm fallback)
  const handleInternalFormSubmit = async (submission) => {
    const data = submission?.data || outgoingSubmissionData || {};
    // Derive client metadata from selected customer instead of manual inputs
    const parseAddresses = (addrStr) => {
      if (!addrStr || typeof addrStr !== 'string') return [];
      try {
        return addrStr.split(':').map(part => {
          const segs = part.split(',');
          return {
            address_line_1: (segs[0] || '').trim(),
            address_line_2: (segs[1] || '').trim(),
            city: (segs[2] || '').trim(),
            state: (segs[3] || '').trim(),
            zip: (segs[4] || '').trim()
          };
        }).filter(a => a.address_line_1 || a.city || a.state || a.zip);
      } catch {
        return [];
      }
    };
    const derivedAddresses = parseAddresses(selectedRefCustomerRef.current?.addressString);
    const addrObj = derivedAddresses[0] || null;
    const clientName = (selectedRefCustomerRef.current?.name || '').trim();
    const addrInput = addrObj ? [addrObj.address_line_1, addrObj.city && addrObj.city + ',', addrObj.state, addrObj.zip].filter(Boolean).join(' ') : '';
    // Company name comes from com_name prop; no manual input required
    // Referring customer selection is mandatory\
    console.log();

    if (!(selectedRefCustomerRef.current && selectedRefCustomerRef.current.cus_id)) {
      setRefCustomerValidationError('Please select the customer to refer.');
      alert('Please select the customer to refer.');
      return;
    }
    // No manual client name/address inputs; we use selected customer's data
    try {
      const subject = [data.subject, clientName].filter(Boolean).join(' - ') || 'Referral';
      const enrichedData = { ...data };
      if (clientName) enrichedData.clientName = clientName;
      if (addrObj) {
        enrichedData.clientAddress = addrObj;
        enrichedData.clientAddressSingleLine = addrInput;
      }
      if (selectedRefCustomerRef.current?.cus_id) {
        enrichedData.referring_customer_id = selectedRefCustomerRef.current.cus_id;
        enrichedData.referring_customer_name = selectedRefCustomerRef.current.name;
      }
      const payload = {
        initiating_company: comp_id,
        receiving_company: selectedCompany?.id,
        referral_subject: subject,
        referral_date: moment().format('YYYY-MM-DD'),
        referral_status: 'PENDING',
        data: enrichedData,
        referral_coordinator_emp_id: activeSetting?.referral_coordinator_emp_id || undefined,
        referral_email: activeSetting?.referral_coordinator_emp_email || undefined,
        receiving_company_name: selectedCompany?.name || undefined,
        client_name: clientName || undefined,
      };
      const resp = await axios.post(`${APIURL}/referral`, payload, { headers: { 'Content-Type': 'application/json' } });
      console.log(resp);

      const ok = (resp?.status >= 200 && resp?.status < 300) || resp?.data?.code === 200;
      if (ok) {
        setSubmitted(true);
        if (onSuccess) onSuccess();
        const referralId = resp?.data?.referral_id;
        // Create coordinator task if configured
        try {
          const hasWorkflow = !!(activeSetting && activeSetting.referral_flow_id);
          const hasCoordinator = !!(activeSetting && activeSetting.referral_coordinator_emp_id);
          // Only create inbound referral task when workflow id is not set
          if (!hasWorkflow && hasCoordinator && referralId) {
            const addressArray = derivedAddresses.length ? derivedAddresses : (addrObj ? [addrObj] : []);
            const taskPayload = {
              employee_id: activeSetting.referral_coordinator_emp_id,
              company_id: selectedCompany?.id,
              customer_name: clientName || undefined,
              address: addressArray,
              initiating_company_name: (com_name || '').trim() || undefined
            };
            const inboundResp = await axios.post(`${APIURL}/referral/${referralId}/task`, taskPayload, { headers: { 'Content-Type': 'application/json' } });
            console.log('Created inbound referral coordinator task');
          }
        } catch (taskErr) {
          // non-blocking
          // console.error('Error creating referral task', taskErr);
        }
        // Always attempt outbound referral task for initiating company using dropdown-selected customer
        try {
          const dropdownCustomerId = selectedRefCustomerRef.current?.cus_id;
          console.log('Dropdown customer id for outbound task:', dropdownCustomerId);
          if (dropdownCustomerId) {
            const outResp = await axios.post(`${APIURL}/referral/outboundTask`, {
              company_id: comp_id,
              employee_id: user?.emp_id,
              customer_id: dropdownCustomerId,
              receiving_company_id: selectedCompany?.id
            }, { headers: { 'Content-Type': 'application/json' } });
            console.log('Created outbound referral task (internal)', outResp?.data);
          } else {
            console.warn('No referring customer selected; skipping outbound task');
          }
        } catch (outErr) {
          console.warn('Failed to create outbound referral task (internal)', outErr?.response?.data || outErr?.message);
        }
        // Create workflow instance if configured (create customer first, then instance)
        try {
          if (activeSetting && activeSetting.referral_flow_id && activeSetting.referral_coordinator_emp_id && clientName && addrObj) {
            const addressArray = [addrObj];
            const customerPayload = {
              name: clientName,
              address: addressArray,
              company_id: selectedCompany?.id,
              status: 'Active',
              customer_type: 'Individual',
              create_project: true,
              empId: activeSetting.referral_coordinator_emp_id
            };
            const custResp = await axios.post(`${APIURL}/customers`, customerPayload, { headers: { 'Content-Type': 'application/json' } });
            const newCustomerId = custResp?.data?.id || custResp?.data?.cus_id;
            if (!newCustomerId) {
              console.warn('Customer creation did not return an id; skipping workflow instance');
            } else {
              await axios.post(`${APIURL}/workflows/instance`, {
                wf_instanceWF_ID: activeSetting.referral_flow_id,
                wf_instanceStatus: 0,
                wf_instanceEmployee: activeSetting.referral_coordinator_emp_id,
                wf_instanceStartDate: moment().format('MM-DD-YYYY'),
                wf_instanceEndDate: moment().add(30, 'days').format('MM-DD-YYYY'),
                wf_instanceCreatedBy: activeSetting.referral_coordinator_emp_id,
                wf_instanceCustomer: newCustomerId,
                company_id: selectedCompany?.id,
              }, { headers: { 'Content-Type': 'application/json' } });
              console.log('Created referral workflow instance');
            }
          }
        } catch (flowErr) {
          // non-blocking
          // console.error('Error creating referral workflow', flowErr);
        }
      } else {
        alert('Failed to submit referral');
      }
    } catch (e) {
      alert('Error submitting referral');
    }
  };

  // Wrapper submit function that awaits the internal submit handler
  const submitInternalReferral = async (submission, e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    await handleInternalFormSubmit(submission);
  };

  return (
    <Paper variant="outlined" sx={{ height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {submitted ? (
        <Box sx={{ p: 3, m: 3, maxWidth: 640, borderRadius: 2, boxShadow: '0 4px 18px rgba(0,0,0,0.06)', background: '#fff', border: '1px solid #e2e8f0' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Referral Submitted</Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
            Your referral has been created.
          </Typography>
          <Button variant="outlined" onClick={() => { setSubmitted(false); resetInternal(); clearExternalSelection(); }}>Back to Create</Button>
        </Box>
      ) : (
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ flex: 1, minHeight: 0 }}>
          {/* LEFT PANE — Partner Directory */}
          <Box
            sx={{
              width: { xs: '100%', md: '42%' },
              p: 2.5,
              borderRight: { md: '1px solid #e0e6ef' },
              borderBottom: { xs: '1px solid #e0e6ef', md: 'none' },
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Referral Creation</Typography>
            <Typography variant="caption" sx={{ color: '#666', mb: 0.5, fontWeight: 600 }}>
              Company Name or GUID
            </Typography>
            <TextField
              placeholder="Search by company name or GUID"
              size="small"
              fullWidth
              value={companyInput}
              onChange={(e) => handleCompanyInputChange(null, e.target.value, 'input')}
              error={!!companyError}
              helperText={companyError || ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#64748b' }} />
                  </InputAdornment>
                ),
                endAdornment: companyInput ? (
                  <InputAdornment position="end">
                    {companyLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    <IconButton size="small" onClick={() => handleCompanyInputChange(null, '', 'clear')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Partner Directory List</Typography>
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pr: 0.5 }}>
              {companyLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : companyOptions.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', color: '#888', border: '1px dashed #d8e0ec', borderRadius: 1.5 }}>
                  <Typography variant="body2">
                    {companyInput.trim().length < 2
                      ? 'Type at least 2 characters to search the partner directory.'
                      : 'No partners found.'}
                  </Typography>
                </Box>
              ) : (
                companyOptions.map((opt) => {
                  const optKey = opt.company_id ? `int-${opt.company_id}` : `ext-${opt.external_company_id || opt.company_name}`;
                  const isInternal = opt.source_type === 'internal';
                  const hasCustomForm = isInternal && !!opt.referral_form_incoming_id;
                  const isSelected = isInternal
                    ? selectedCompany?.id === opt.company_id
                    : externalSelectedFromSearch && extCompanyNameRef.current === opt.company_name;
                  return (
                    <Card
                      key={optKey}
                      variant="outlined"
                      sx={{
                        mb: 1,
                        borderColor: isSelected ? '#3fa7d6' : '#e0e6ef',
                        bgcolor: isSelected ? '#eaf6fd' : '#fff',
                        transition: 'border-color 120ms, background-color 120ms',
                      }}
                    >
                      <CardActionArea onClick={() => handleCompanySelect(null, opt)} sx={{ p: 1.25 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar
                            src={opt.company_id ? (companyLogos[opt.company_id] || undefined) : undefined}
                            imgProps={{ style: { objectFit: 'contain' } }}
                            sx={{
                              bgcolor: hasCustomForm ? '#3fa7d6' : isInternal ? '#5a6a85' : '#7b61ff',
                              width: 40,
                              height: 40,
                              fontWeight: 700,
                              fontSize: '1rem',
                            }}
                          >
                            {(opt.company_name || '?').charAt(0).toUpperCase()}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#222' }} noWrap>
                              {opt.company_name}
                            </Typography>
                            {opt.company_guid && (
                              <Typography variant="caption" sx={{ color: '#888' }} noWrap>
                                {opt.company_guid}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            size="small"
                            icon={<DescriptionOutlinedIcon />}
                            label={hasCustomForm ? 'Custom Form' : 'Generic Form'}
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              borderColor: hasCustomForm ? '#3fa7d6' : isInternal ? '#5a6a85' : '#7b61ff',
                              color: hasCustomForm ? '#3fa7d6' : isInternal ? '#5a6a85' : '#7b61ff',
                              '& .MuiChip-icon': { color: hasCustomForm ? '#3fa7d6' : isInternal ? '#5a6a85' : '#7b61ff' },
                            }}
                          />
                        </Stack>
                      </CardActionArea>
                    </Card>
                  );
                })
              )}
            </Box>
          </Box>

          {/* RIGHT PANE — Active Referral Workspace */}
          <Box sx={{ flex: 1, p: 2.5, overflowY: 'auto', minHeight: 0 }}>
            {selectedCompany ? (
              /* ── INTERNAL COMPANY SELECTED ── */
              <Stack spacing={2}>
                {/* Banner */}
                <Card variant="outlined" sx={{ borderColor: '#e0e6ef' }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FiberManualRecordIcon sx={{ color: '#dc3545', fontSize: 14 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Target Company: {selectedCompany.name}
                        {companyOptions.find(o => o.company_id === selectedCompany.id)?.referral_form_incoming_id ? ' (Custom Form)' : ' (No Custom Form)'}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Customer dropdown */}
                <Autocomplete
                  options={Array.isArray(refCustomerOptions) ? refCustomerOptions : []}
                  loading={refCustomerLoading}
                  defaultValue={preselectedCustomer || null}
                  onChange={(_e, v) => { selectedRefCustomerRef.current = v; setRefCustomerValidationError(null); }}
                  getOptionLabel={(opt) => opt?.name || ''}
                  isOptionEqualToValue={(option, value) => option?.cus_id === value?.cus_id}
                  disabled={!!preselectedCustomer}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer to refer"
                      required
                      size="small"
                      fullWidth
                      error={!!refCustomerValidationError || !!refCustomerError}
                      helperText={refCustomerValidationError || refCustomerError || ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {refCustomerLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {/* Internal form area */}
                {incomingFormLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : incomingFormError ? (
                  <Alert severity="error">{incomingFormError}</Alert>
                ) : incomingFormSchema ? (
                  <FormioForm
                    key={`incoming-form-${selectedCompany.id}`}
                    form={incomingFormSchema}
                    onChange={(submission) => setOutgoingSubmissionData(submission?.data || null)}
                    onSubmit={submitInternalReferral}
                    options={{
                      noAlerts: false,
                      buttonSettings: { showCancel: false },
                      submitOnEnter: true,
                      breadcrumbSettings: { clickable: true },
                      editForm: { feedback: false },
                    }}
                  />
                ) : (
                  <Box>
                    <OBForm
                      form={{}}
                      onChange={(e) => {
                        const { name, value, type, checked } = e?.target || {};
                        if (!name) return;
                        obFormDataRef.current[name] = type === 'checkbox' ? !!checked : value;
                      }}
                      apiBase={APIURL}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button variant="contained" onClick={(e) => submitInternalReferral({ data: obFormDataRef.current }, e)}>
                        Submit Referral
                      </Button>
                    </Box>
                  </Box>
                )}
              </Stack>
            ) : (
              /* ── GENERIC / EXTERNAL REFERRAL (default + external company from search) ── */
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {externalSelectedFromSearch ? 'Referral to Partner' : 'Generic Referral'}
                </Typography>

                {/* If an external company was picked from the directory, show banner + Change button */}
                {externalSelectedFromSearch ? (
                  <Card variant="outlined" sx={{ borderColor: '#e0e6ef' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <FiberManualRecordIcon sx={{ color: '#dc3545', fontSize: 14 }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {extCompanyNameRef.current || 'Unknown'}
                          </Typography>
                        </Stack>
                        <Button size="small" variant="text" color="error" startIcon={<CloseIcon />} onClick={clearExternalSelection}>
                          Change
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ) : (
                  /* Company text fields — shown when no company is pre-selected */
                  <Stack spacing={1.5}>
                    <TextField
                      label="Company Name"
                      required
                      size="small"
                      fullWidth
                      value={extCompanyNameRef.current}
                      onChange={(e) => { extCompanyNameRef.current = e.target.value; forceExternalFieldsUpdate(t => t + 1); }}
                    />
                    <TextField
                      label="Company Address"
                      size="small"
                      fullWidth
                      value={extCompanyAddressRef.current}
                      onChange={(e) => { extCompanyAddressRef.current = e.target.value; forceExternalFieldsUpdate(t => t + 1); }}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <TextField
                        label="Company Email"
                        type="email"
                        size="small"
                        sx={{ flex: 1 }}
                        value={extCompanyEmailRef.current}
                        onChange={(e) => { extCompanyEmailRef.current = e.target.value; forceExternalFieldsUpdate(t => t + 1); }}
                      />
                      <TextField
                        label="Company Phone"
                        size="small"
                        sx={{ flex: 1 }}
                        value={extCompanyPhoneRef.current}
                        onChange={(e) => { extCompanyPhoneRef.current = e.target.value; forceExternalFieldsUpdate(t => t + 1); }}
                      />
                    </Stack>
                  </Stack>
                )}

                {/* Customer dropdown */}
                <Autocomplete
                  options={Array.isArray(refCustomerOptions) ? refCustomerOptions : []}
                  loading={refCustomerLoading}
                  defaultValue={preselectedCustomer || null}
                  onChange={(_e, v) => { selectedRefCustomerRef.current = v; setRefCustomerValidationError(null); }}
                  getOptionLabel={(opt) => opt?.name || ''}
                  isOptionEqualToValue={(option, value) => option?.cus_id === value?.cus_id}
                  disabled={!!preselectedCustomer}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer to refer"
                      required
                      size="small"
                      fullWidth
                      error={!!refCustomerValidationError || !!refCustomerError}
                      helperText={refCustomerValidationError || refCustomerError || ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {refCustomerLoading ? <CircularProgress size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {/* External form area */}
                <Box sx={{ position: 'relative' }}>
                  {externalFormLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : externalFormError ? (
                    <Alert severity="error">{externalFormError}</Alert>
                  ) : externalFormSchema ? (
                    <FormioForm
                      form={externalFormSchema}
                      onSubmit={(submission) => handleExternalReferralSubmit(null, submission?.data)}
                      options={{
                        noAlerts: false,
                        buttonSettings: { showCancel: false },
                        submitOnEnter: true,
                        breadcrumbSettings: { clickable: true },
                        editForm: { feedback: false },
                      }}
                    />
                  ) : (
                    <SocialCareReferralForm
                      form={extSocialForm}
                      onChange={(e) => {
                        const { name, value, type, checked } = e.target || {};
                        if (!name) return;
                        setExtSocialForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
                      }}
                      onSubmit={(e) => handleExternalReferralSubmit(e, extSocialForm)}
                    />
                  )}
                  {extSubmitting && (
                    <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  )}
                </Box>
              </Stack>
            )}
          </Box>
        </Stack>
      )}
    </Paper>
  );
};

export default CreateReferral;
